import React from "react";
import { useTranslation } from "react-i18next";
import {
  BookOpen,
  Flame,
  TrendingUp,
  ShoppingBag,
  Bell,
  MoreHorizontal,
  Trash2,
  CheckCheck,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import type { Notification, NotificationIconType } from "@/stores/useNotificationStore";
import { formatTimeAgo } from "@/stores/useNotificationStore";

/** Compute notification title and action at render time from kind + payload. Never use stored translated strings. */
function getNotificationDisplayText(
  notification: Notification,
  t: (key: string, defaultText?: string, vars?: Record<string, string | number>) => string
): { title: string; action: string } {
  const kind = notification.kind;
  const payload = notification.payload ?? {};

  if (!kind) {
    return {
      title: notification.title ?? "",
      action: notification.actionText ?? "",
    };
  }

  switch (kind) {
    case "WORKOUT_COMPLETED":
      return {
        title: t("notif_workout_completed", "Training complete! +{{crystals}} 💎, +{{xp}} XP", { crystals: payload.crystals ?? 0, xp: payload.xp ?? 0 }),
        action: t("notif_keep_going", "Keep it up!"),
      };
    case "RANK_UP": {
      const rankId = (payload.rankId as string) ?? "";
      const rankNameResolved = rankId ? t(`achievements.shop.items.${rankId}`) : "";
      return {
        title: t("notif_rank_up", "Congratulations! You reached the rank {{rankName}}.", { rankName: rankNameResolved }),
        action: t("notif_rank_up_action", "Check your progress."),
      };
    }
    case "STREAK_MILESTONE":
      return {
        title: t("gamification.medalUnlocked", "Medal Unlocked!"),
        action: t("gamification.streakMilestone", "{{count}}-day streak!", { count: payload.count ?? 0 }),
      };
    case "STUDY_REWARD":
      return {
        title: t("notif_study_reward", "Training complete! You earned +{{crystals}} crystals.", { crystals: payload.crystals ?? 0 }),
        action: t("notif_study_reward_action", "Spend them in the shop."),
      };
    case "STREAK_WARNING":
      return {
        title: t("notif_streak_warning", "Your streak is at risk!"),
        action: t("notif_streak_warning_action", "Complete one deck to save your streak."),
      };
    case "SPACED_REPETITION":
      return {
        title: t("notif_spaced_repetition", "You studied \"{{deckName}}\" a few days ago.", { deckName: payload.deckName ?? "" }),
        action: t("notif_spaced_repetition_action", "Review it now to reinforce your memory."),
      };
    case "MISTAKE_REVIEW":
      return {
        title: t("notif_mistake_review", "There were errors in the test \"{{deckName}}\".", { deckName: payload.deckName ?? "" }),
        action: t("notif_mistake_review_action", "Redo the difficult cards to close the gaps."),
      };
    case "WEEKLY_INSIGHT":
      return {
        title: t("notif_weekly_insight", "Great pace! This week you learned {{wordsCount}} new words.", { wordsCount: payload.wordsCount ?? 0 }),
        action: t("notif_weekly_insight_action", "Complete another deck to improve your result."),
      };
    case "MICRO_LEARNING":
      return {
        title: t("notif_micro_learning", "Got 5 free minutes?"),
        action: t("notif_micro_learning_action", "Take a quick 10-card training session."),
      };
    case "TEST_COMPLETE":
      return {
        title: t("notif_test_complete", "Test completed!"),
        action: t("notif_test_complete_action", "You completed a test of 10+ cards and earned rewards!"),
      };
    case "MEDAL_TIME_MASTER":
      return {
        title: t("notif_medal_time_master", "Medal «Time Master» unlocked!"),
        action: t("notif_study_time_label", "Study time: {{label}}. Keep it up!", { label: payload.label ?? "" }),
      };
    case "MEDAL_APP_CHAMPION":
      return {
        title: t("notif_medal_app_champion", "Medal «App Champion» unlocked!"),
        action: t("notif_app_time_label", "Total app time: {{label}}. Great progress!", { label: payload.label ?? "" }),
      };
    case "DUMMY_RETURN":
      return {
        title: t("notif_dummy_return", "Return to studying the deck \"English B1\"."),
        action: t("notif_dummy_return_action", "Keep up the great work!"),
      };
    case "DUMMY_STREAK":
      return {
        title: t("notif_dummy_streak", "Great! You've built a study chain."),
        action: t("notif_dummy_streak_action", "Don't slow down, keep learning."),
      };
    case "DUMMY_LEVEL":
      return {
        title: t("notif_dummy_level", "New mastery level!"),
        action: t("notif_dummy_level_action", "You've reached the rank \"Connoisseur\". Check your awards."),
      };
    case "DUMMY_REPEAT":
      return {
        title: t("notif_dummy_repeat", "You studied the deck \"English B1\" a few days ago."),
        action: t("notif_dummy_repeat_action", "Review it now to reinforce your memory."),
      };
    case "DUMMY_MICRO":
      return {
        title: t("notif_dummy_micro", "Got 5 free minutes?"),
        action: t("notif_dummy_micro_action", "Take a quick 10-card training session."),
      };
    default:
      return { title: "", action: "" };
  }
}

const ICON_CONFIG: Record<
  NotificationIconType,
  { Icon: React.ElementType; bgClass: string; iconClass: string }
> = {
  study: {
    Icon: BookOpen,
    bgClass: "bg-blue-100 dark:bg-blue-900/40",
    iconClass: "text-blue-500 dark:text-blue-400",
  },
  streak: {
    Icon: Flame,
    bgClass: "bg-orange-100 dark:bg-orange-900/40",
    iconClass: "text-orange-500 dark:text-orange-400",
  },
  achievement: {
    Icon: TrendingUp,
    bgClass: "bg-purple-100 dark:bg-purple-900/40",
    iconClass: "text-purple-500 dark:text-purple-400",
  },
  shop: {
    Icon: ShoppingBag,
    bgClass: "bg-emerald-100 dark:bg-emerald-900/40",
    iconClass: "text-emerald-600 dark:text-emerald-400",
  },
  reminder: {
    Icon: Bell,
    bgClass: "bg-slate-100 dark:bg-slate-800",
    iconClass: "text-slate-500 dark:text-slate-400",
  },
};

function NotificationItem({
  notification,
  onDelete,
  onMarkAsRead,
  onNotificationClick,
  t,
}: {
  notification: Notification;
  onDelete: (id: string) => void;
  onMarkAsRead: (id: string) => void;
  onNotificationClick: (notification: Notification) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const handleClick = () => {
    onNotificationClick(notification);
  };

  const config = ICON_CONFIG[notification.iconType];
  const { Icon } = config;

  const { title: displayTitle, action: displayAction } = getNotificationDisplayText(notification, t);
  const timeAgoText = formatTimeAgo(notification.createdAt);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleClick();
      }}
      className={cn(
        "relative flex gap-3 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer",
        "border-b border-slate-100 dark:border-slate-800 last:border-0 transition-colors"
      )}
    >
      {/* Unread dot */}
      {!notification.isRead && (
        <div
          className="absolute left-2 top-5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"
          aria-hidden
        />
      )}

      {/* Icon container */}
      <div
        className={cn(
          "shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
          config.bgClass,
          config.iconClass
        )}
      >
        <Icon size={20} strokeWidth={2} />
      </div>

      {/* Text content — translated at render time for current locale */}
      <div className="flex-1 min-w-0 flex flex-col">
        <p className="text-sm text-slate-600 dark:text-slate-300 leading-snug">
          {displayTitle}{" "}
          <span className="font-semibold text-slate-900 dark:text-white">
            {displayAction}
          </span>
        </p>
        <span className="text-xs text-slate-400 dark:text-slate-500 mt-1">
          {timeAgoText}
        </span>
      </div>

      {/* More options dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "flex-shrink-0 p-1.5 rounded-md transition-colors",
              "text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-slate-300"
            )}
            aria-label={t("common.moreOptions")}
          >
            <MoreHorizontal size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={4}
          className="min-w-[180px] !bg-white !text-slate-900 border border-slate-200 shadow-lg dark:!bg-slate-900 dark:!text-slate-100 dark:border-slate-700"
        >
          {!notification.isRead && (
            <DropdownMenuItem
              onSelect={(e) => {
                e.stopPropagation();
                onMarkAsRead(notification.id);
              }}
              className="cursor-pointer focus:bg-slate-50 dark:focus:bg-slate-800"
            >
              <CheckCheck size={14} className="mr-2" />
              {t("notifications.markAsRead")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={(e) => {
              e.stopPropagation();
              onDelete(notification.id);
            }}
            className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/20 cursor-pointer"
          >
            <Trash2 size={14} className="mr-2" />
            {t("notifications.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

interface NotificationsPopoverProps {
  trigger: React.ReactNode;
  notifications: Notification[];
  onDeleteNotification: (id: string) => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onNotificationClick: (notification: Notification) => void;
  side?: "top" | "right" | "bottom" | "left";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function NotificationsPopover({
  trigger,
  notifications,
  onDeleteNotification,
  onMarkAsRead,
  onMarkAllAsRead,
  onNotificationClick,
  side = "bottom",
  open,
  onOpenChange,
}: NotificationsPopoverProps) {
  const { t } = useTranslation();
  const hasUnread = notifications.some((n) => !n.isRead);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        side={side}
        sideOffset={8}
        className="w-[min(380px,calc(100vw-1rem))] p-0 !bg-white !text-slate-900 border border-slate-200 shadow-xl rounded-xl z-50 dark:!bg-slate-900 dark:!text-slate-100 dark:border-slate-700"
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
          <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
            {t("notifications.title")}
          </h3>
          {hasUnread && (
            <button
              type="button"
              onClick={onMarkAllAsRead}
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors flex-shrink-0 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {t("notifications.markAllAsRead")}
            </button>
          )}
        </div>
        <ScrollArea className="h-[360px]">
          <div className="py-0">
            {notifications.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                {t("notifications.empty")}
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onDelete={onDeleteNotification}
                  onMarkAsRead={onMarkAsRead}
                  onNotificationClick={onNotificationClick}
                  t={t}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
